/**
 * Puntaje GuardIA — `guardia-score-v1`.
 *
 * ## De dónde sale, y de dónde NO sale
 *
 * El informe que Grupo Arcos venía entregando mostraba un 63,0 sobre TST102.
 * Ese número **no tiene origen en ningún repositorio de la organización**: se
 * buscó la fórmula en `guardia-platform-sites` y en `guardia-drive` y no
 * existe. Lo que sí se pudo verificar es el resto de sus cifras (26,97 por 100
 * km, 52,2 % de giros, 47,8 % de aceleraciones), que salen exactas de la
 * aritmética de este motor.
 *
 * Así que aquí no se reconstruye una fórmula perdida: se escribe una, entera y
 * a la vista, y se la ancla al único punto conocido para que el informe no
 * cambie de escala de un día para otro.
 *
 * ## La fórmula
 *
 *     descuento = escala × Σ (peso_familia × tasa_familia_por_100km)
 *     puntaje   = 100 − descuento, acotado entre 0 y 100
 *
 * Tres decisiones, cada una con su razón:
 *
 *  1. **Por 100 km y no por hora.** El equipo sigue reportando parqueado; una
 *     tasa por hora mediría dónde durmió el carro. TST102 promedió 1,3 km/h en
 *     un período de 15 días.
 *  2. **Solo episodios moderados o más.** Un episodio «bajo» es una señal
 *     técnica, no un hecho del conductor. Descontar por él castigaría el ruido.
 *  3. **Solo familias que se pudieron medir.** Si a esta cadencia el detector
 *     de frenadas no alcanza a dispararse, su cero significa «no medimos» y no
 *     puede sumar cero al descuento como si fuera mérito. El puntaje sale
 *     marcado como parcial y dice qué faltó.
 *
 * ## Lo que este puntaje NO es
 *
 * No es actuarial, no predice siniestros, no es un puntaje de aseguradora y no
 * sirve para tarificar. Es una medida de frecuencia de maniobras bruscas por
 * distancia, y solo tiene sentido comparado consigo mismo entre períodos del
 * mismo vehículo o entre vehículos de la misma cadencia.
 *
 * Y mientras el perfil que lo alimenta esté `UNCALIBRATED`, el puntaje hereda
 * esa condición: los umbrales deciden cuántos episodios hay, y los episodios
 * deciden el puntaje.
 */

export const GUARDIA_SCORE_VERSION = "guardia-score-v1" as const;

export type ScoreWeights = {
  acceleration: number;
  braking: number;
  turn: number;
  elevatedSpeed: number;
  /**
   * Descuento por cada punto de tasa agregada.
   *
   * 1,372 es un **anclaje, no una calibración**: es el valor con el que el
   * informe que se venía entregando daba 63,0 sobre TST102 (767,49 km, 207
   * eventos, 26,97 por 100 km). Se fija para que la escala del puntaje no
   * cambie de un día para otro, y queda a la vista para que Grupo Arcos lo
   * mueva cuando decida qué debe significar un 70.
   */
  scale: number;
};

export const DEFAULT_WEIGHTS: ScoreWeights = {
  // Pesos iguales a propósito. Decir que una frenada brusca «vale» 1,4 veces un
  // giro brusco sería una afirmación sobre riesgo que estos datos no sostienen
  // (constitución, Art. 3). Cuando haya siniestralidad con la que contrastarlo,
  // estos cuatro números son lo que hay que mover.
  acceleration: 1,
  braking: 1,
  turn: 1,
  elevatedSpeed: 1,
  scale: 1.372,
};

export type ScoreBand = {
  from: number;
  label: string;
  meaning: string;
};

/**
 * Eje del medidor, tal como se dibuja en el informe: cinco tramos iguales.
 * Es la escala visual, distinta del estado que se le pone al número.
 */
export const SCORE_GAUGE_LABELS = ["Crítico", "Atención", "Observación", "Bueno", "Excelente"] as const;

/**
 * Estado del período, de mejor a peor.
 *
 * Los cortes están puestos para que coincidan con el informe que Grupo Arcos
 * venía entregando: allí un 63,0 se rotulaba «Prioridad de mejora». No es un
 * detalle cosmético — si el mismo número cambiara de estado al formalizar la
 * fórmula, un cliente que compare dos informes seguidos vería una mejora que
 * no ocurrió.
 */
export const SCORE_BANDS: ScoreBand[] = [
  { from: 90, label: "Excelente", meaning: "La conducción del período fue consistentemente suave." },
  { from: 78, label: "Bueno", meaning: "Buena conducción general, con episodios aislados." },
  { from: 66, label: "Observación", meaning: "Aparecen comportamientos que conviene observar." },
  { from: 45, label: "Prioridad de mejora", meaning: "Se identificaron comportamientos recurrentes que requieren atención prioritaria." },
  { from: 0, label: "Crítico", meaning: "La frecuencia de maniobras bruscas es muy alta para la distancia recorrida." },
];

export function bandFor(score: number): ScoreBand {
  return SCORE_BANDS.find((band) => score >= band.from) ?? SCORE_BANDS[SCORE_BANDS.length - 1]!;
}

/** Nivel de atención, leído sobre la tasa agregada de eventos relevantes. */
export const ATTENTION_BANDS = [
  { from: 20, label: "Alto", note: "Riesgo alto" },
  { from: 8, label: "Medio", note: "Riesgo medio" },
  { from: 0, label: "Bajo", note: "Riesgo bajo" },
] as const;

export function attentionFor(ratePer100Km: number | null) {
  if (ratePer100Km === null) return { label: "No evaluado", note: "Sin distancia suficiente" };
  return ATTENTION_BANDS.find((band) => ratePer100Km >= band.from) ?? ATTENTION_BANDS[2];
}

/**
 * Motivo por el que no hay puntaje. Cada uno se explica en el informe.
 *
 * `PERFIL_SIN_CALIBRAR` es el que más importa y el que costó verlo en datos
 * reales: TST103 en la primera semana de julio dio 123 episodios moderados o
 * más en 146 km — 84 por cada 100 km, uno cada 1,2 km— y el puntaje tocó fondo
 * en 0,0 «Crítico». Ningún conductor real maneja así; lo que pasa es que los
 * umbrales provisionales marcan como brusca una maniobra normal.
 *
 * Un 0,0 en tipografía grande con la palabra «Crítico» debajo no lo rescata
 * ninguna nota al pie: nadie lee la nota, y ese informe se le entrega a una
 * persona. Mientras el perfil esté sin calibrar, el puntaje no se publica.
 * Los conteos y las tasas sí, porque esos sí son hechos de la medición.
 */
export type NoScoreReason = "SIN_DISTANCIA" | "PERFIL_SIN_CALIBRAR";

export type FamilyInput = {
  key: "acceleration" | "braking" | "turn" | "elevatedSpeed";
  /** Episodios moderados o más de esa familia. */
  relevant: number;
  /** `true` si la cadencia no permitió medir esta familia. */
  blind: boolean;
};

export type GuardiaScore = {
  version: typeof GUARDIA_SCORE_VERSION;
  /** `null` cuando no hay distancia suficiente: no se publica un puntaje sin denominador. */
  score: number | null;
  band: ScoreBand | null;
  distanceKm: number;
  /** Tasa agregada de episodios relevantes de las familias medidas. */
  relevantRatePer100Km: number | null;
  attention: { label: string; note: string };
  /** Aporte de cada familia al descuento. Es lo que se muestra en las barras. */
  contributions: {
    key: FamilyInput["key"];
    relevant: number;
    ratePer100Km: number | null;
    deduction: number;
    /** Porcentaje del descuento total. `null` si no hubo descuento. */
    sharePercent: number | null;
    blind: boolean;
  }[];
  /** Por qué no hay puntaje, cuando `score` es `null`. */
  noScoreReason: NoScoreReason | null;
  /** `true` si alguna familia quedó fuera por no poder medirse. */
  partial: boolean;
  blindFamilies: FamilyInput["key"][];
  weights: ScoreWeights;
  /** Por qué el puntaje es lo que es, en una frase. */
  explanation: string;
};

/** Distancia mínima para publicar un puntaje. Misma razón que en la flota. */
export const MINIMUM_SCORE_DISTANCE_KM = 20;

const round1 = (value: number) => Math.round(value * 10) / 10;
const round2 = (value: number) => Math.round(value * 100) / 100;

export function computeGuardiaScore(input: {
  families: FamilyInput[];
  distanceKm: number;
  weights?: ScoreWeights;
  /**
   * `false` mientras el perfil aplicado tenga umbrales provisionales. Sin esto
   * el puntaje sale como si fuera una medición, y no lo es: los umbrales
   * deciden cuántos episodios hay, y los episodios deciden el puntaje.
   */
  calibrated?: boolean;
}): GuardiaScore {
  const weights = input.weights ?? DEFAULT_WEIGHTS;
  const distanceKm = input.distanceKm;
  const calibrated = input.calibrated ?? true;
  const enough = distanceKm >= MINIMUM_SCORE_DISTANCE_KM;

  const contributions = input.families.map((family) => {
    const rate = enough && distanceKm > 0 ? (family.relevant / distanceKm) * 100 : null;
    const deduction = family.blind || rate === null ? 0 : weights[family.key] * rate * weights.scale;
    return {
      key: family.key,
      relevant: family.relevant,
      ratePer100Km: rate === null ? null : round2(rate),
      deduction: round2(deduction),
      sharePercent: null as number | null,
      blind: family.blind,
    };
  });

  const totalDeduction = contributions.reduce((sum, item) => sum + item.deduction, 0);
  for (const item of contributions) {
    item.sharePercent = totalDeduction > 0 ? round1((item.deduction / totalDeduction) * 100) : null;
  }

  const measuredRelevant = input.families
    .filter((family) => !family.blind)
    .reduce((sum, family) => sum + family.relevant, 0);
  const relevantRate = enough && distanceKm > 0 ? round2((measuredRelevant / distanceKm) * 100) : null;

  const blindFamilies = input.families.filter((family) => family.blind).map((family) => family.key);

  const noScoreReason: NoScoreReason | null = !enough
    ? "SIN_DISTANCIA"
    : !calibrated
      ? "PERFIL_SIN_CALIBRAR"
      : null;
  const score = noScoreReason === null ? Math.max(0, Math.min(100, round1(100 - totalDeduction))) : null;

  const explanation = !enough
    ? `Se recorrieron ${round1(distanceKm)} km, por debajo de los ${MINIMUM_SCORE_DISTANCE_KM} km mínimos. Con tan poca distancia un episodio suelto mueve el puntaje varios puntos, así que no se publica.`
    : !calibrated
      ? `Se midieron ${measuredRelevant} episodios moderados o más en ${round1(distanceKm)} km, que son ${relevantRate} por cada 100 km. ` +
        "Esa cifra es un hecho de la medición y se publica. El puntaje NO, porque los umbrales de este perfil todavía son provisionales: " +
        "son ellos los que deciden qué cuenta como maniobra brusca, así que un puntaje calculado con ellos diría más de los umbrales que del conductor. " +
        "Sale en cuanto el perfil se calibre en el banco de pruebas."
      : `${measuredRelevant} episodios moderados o más en ${round1(distanceKm)} km dan ${relevantRate} por cada 100 km. ` +
      `Con el descuento de ${weights.scale} puntos por cada punto de tasa, el puntaje queda en ${score}.` +
      (blindFamilies.length
        ? ` Quedaron fuera ${blindFamilies.length} familia(s) que la cadencia del equipo no permitió medir: su ausencia no suma ni resta.`
        : "");

  return {
    version: GUARDIA_SCORE_VERSION,
    score,
    band: score === null ? null : bandFor(score),
    distanceKm: round2(distanceKm),
    relevantRatePer100Km: relevantRate,
    attention: attentionFor(relevantRate),
    contributions,
    noScoreReason,
    partial: blindFamilies.length > 0,
    blindFamilies,
    weights,
    explanation,
  };
}
